import React, { useState, useEffect, useMemo } from 'react';
import treatmentService from '../services/TreatmentService';
import { useAuth } from '../contexts/AuthContext';
import { Card, Button, Input, SearchInput, Table, Modal, Select, CustomDropdown, Alert } from '../components/ui';

/**
 * TreatmentsPage component for managing the clinic's treatment offerings
 * @returns {React.ReactElement} TreatmentsPage component
 */
const TreatmentsPage = () => {
  // State for treatments data
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // State for creating/editing treatments
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 60,
    price: '',
    category: '常规调理',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [modalLoading, setModalLoading] = useState(false);

  const { hasPermission } = useAuth();
  const canManageTreatments = hasPermission('treatments', 'write');

  // Fetch treatments on component mount
  useEffect(() => {
    fetchTreatments();
  }, []);

  // Function to fetch treatments
  const fetchTreatments = async () => {
    setLoading(true);
    try {
      const data = treatmentService.getAllTreatments();
      setTreatments(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching treatments:', err);
      setError('获取调理项目数据失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter and search treatments
  const filteredTreatments = useMemo(() => {
    const filtered = treatments.filter(treatment => {
      const matchesSearch =
        (treatment.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        treatment.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (Array.isArray(treatment.applicableConditions) && treatment.applicableConditions.join('、').toLowerCase().includes(searchTerm.toLowerCase())));
      const matchesType = typeFilter === 'all' ? true : treatment.type === typeFilter;
      if (filter === 'all') return matchesSearch && matchesType;
      if (filter === 'active') return matchesSearch && matchesType && treatment.isActive;
      if (filter === 'inactive') return matchesSearch && matchesType && !treatment.isActive;
      return matchesSearch && matchesType;
    });
    // Sort: project first, then basic
    return filtered.sort((a, b) => {
      if (a.type === b.type) return 0;
      if (a.type === 'project') return -1;
      return 1;
    });
  }, [treatments, searchTerm, filter, typeFilter]);

  // Get unique categories for display
  const categories = useMemo(() => {
    const uniqueCategories = new Set();
    treatments.forEach(treatment => {
      if (treatment.category) {
        uniqueCategories.add(treatment.category);
      }
    });
    return Array.from(uniqueCategories);
  }, [treatments]);

  // Handle modal open for new treatment
  const handleAddNewTreatment = () => {
    setSelectedTreatment(null);
    setFormData({
      name: '',
      description: '',
      duration: 60,
      price: '',
      isActive: true,
      type: 'basic',
      applicableConditions: '',
      contraindications: '',
      preparationInstructions: '',
      postTreatmentCare: '',
      composedOf: [],
    });
    setFormErrors({});
    setModalLoading(false);
    setShowTreatmentModal(true);
  };

  // Handle modal open for edit treatment
  const handleEditTreatment = (treatment) => {
    setSelectedTreatment(treatment);
    setFormErrors({});
    setShowTreatmentModal(true);
    setModalLoading(true);
    setTimeout(() => {
      setFormData({
        ...treatment,
        applicableConditions: (treatment.applicableConditions || []).join('、'),
        contraindications: (treatment.contraindications || []).join('、'),
        composedOf: treatment.composedOf || [],
        type: treatment.type || 'basic',
      });
      setModalLoading(false);
    }, 0);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = '请输入项目名称';
    }

    if (!formData.duration || formData.duration < 5) {
      errors.duration = '请输入有效的时长（至少5分钟）';
    }

    if (formData.price && (isNaN(formData.price) || formData.price < 0)) {
      errors.price = '请输入有效的价格';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const submitData = {
      ...formData,
      applicableConditions: formData.applicableConditions
        ? formData.applicableConditions.split('、').filter(Boolean)
        : [],
      contraindications: formData.contraindications
        ? formData.contraindications.split('、').filter(Boolean)
        : [],
    };
    try {
      if (selectedTreatment) {
        treatmentService.updateTreatment(selectedTreatment.id, submitData);
      } else {
        treatmentService.createTreatment(submitData);
      }
      fetchTreatments();
      setShowTreatmentModal(false);
    } catch (err) {
      console.error('Error saving treatment:', err);
      setFormErrors({ submit: '保存调理项目失败，请重试' });
    }
  };

  // Handle treatment delete
  const handleDeleteTreatment = (treatmentId) => {
    if (window.confirm('确定要删除此调理项目吗？此操作无法撤销。')) {
      try {
        treatmentService.deleteTreatment(treatmentId);
        fetchTreatments();
      } catch (err) {
        console.error('Error deleting treatment:', err);
        setError('删除调理项目失败');
      }
    }
  };

  // Handle toggle treatment status
  const handleToggleStatus = (treatment) => {
    try {
      const updatedTreatment = {
        ...treatment,
        isActive: !treatment.isActive
      };
      treatmentService.updateTreatment(treatment.id, updatedTreatment);
      fetchTreatments();
    } catch (err) {
      console.error('Error updating treatment status:', err);
      setError('更新调理项目状态失败');
    }
  };

  // Table columns configuration
  const columns = [
    {
      key: 'name',
      title: '项目信息',
      render: (_, treatment) => (
        <div className="flex items-center space-x-3 max-w-[180px]">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold shadow-lg">
              {treatment.name ? treatment.name.charAt(0) : '?'}
            </div>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900">
                {treatment.name || '未命名项目'}
              </span>
              {/* Type tag first, then status tag */}
              {treatment.type === 'project' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  特色项目
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  基础项目
                </span>
              )}
              {treatment.isActive ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  已启用
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  未启用
                </span>
              )}
            </div>
            {treatment.description && (
              <p className="text-xs text-gray-500 mt-1 truncate" style={{ maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {treatment.description}
              </p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'details',
      title: <span className="ml-8 text-left">时长/价格</span>,
      render: (_, treatment) => (
        <div className="ml-8 flex flex-col items-start" style={{ minWidth: '90px', maxWidth: '110px' }}>
          <div className="text-sm text-gray-900">
            {treatment.duration ? `${treatment.duration}分钟` : '未设定时长'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {treatment.price ? `¥${treatment.price}` : '未定价'}
          </div>
        </div>
      ),
    },
    {
      key: 'applicableConditions',
      title: '适应症',
      render: (_, t) => {
        const text = t.applicableConditions?.join('、') || '-';
        return (
          <div
            className="max-w-[600px] cursor-pointer overflow-hidden"
            title={text}
            style={{
              maxWidth: '600px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {text}
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: '操作',
      render: (_, treatment) => (
        <div className="flex items-center space-x-2">
          {canManageTreatments && (
            <>
              <button
                onClick={() => handleToggleStatus(treatment)}
                className="text-yellow-600 hover:text-yellow-700 text-sm font-medium transition-colors duration-200"
              >
                {treatment.isActive ? '停用' : '启用'}
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => handleEditTreatment(treatment)}
                className="text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors duration-200"
              >
                编辑
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => handleDeleteTreatment(treatment.id)}
                className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors duration-200"
              >
                删除
              </button>
            </>
          )}
        </div>
      ),
      cellClassName: 'text-right'
    }
  ];

  // Filter options
  const filterOptions = [
    { value: 'all', label: '全部项目' },
    { value: 'active', label: '已启用项目' },
    { value: 'inactive', label: '未启用项目' }
  ];

  // Type filter options
  const typeFilterOptions = [
    { value: 'all', label: '全部类型' },
    { value: 'basic', label: '基础项目' },
    { value: 'project', label: '特色项目' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              调理项目管理
            </h1>
            <p className="text-gray-600">
              管理中心的调理项目信息和状态
            </p>
          </div>
          {canManageTreatments && (
            <div className="mt-4 sm:mt-0">
              <Button
                onClick={handleAddNewTreatment}
              >
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加调理项目
              </Button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-2">
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="搜索调理项目名称、描述或适应症..."
                className="w-full"
              />
            </div>
            <div>
              <CustomDropdown
                options={filterOptions}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="选择筛选条件"
              />
            </div>
            <div>
              <CustomDropdown
                options={typeFilterOptions}
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                placeholder="选择类型"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>共找到 {filteredTreatments.length} 个调理项目</span>
            {searchTerm && (
              <span>搜索: "{searchTerm}"</span>
            )}
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Treatments Table */}
        <Table
          columns={columns}
          data={filteredTreatments}
          loading={loading}
          variant="simple"
          className="border-emerald-200/50 bg-white/70 backdrop-blur-sm"
          emptyMessage={searchTerm ? '没有找到匹配的调理项目' : '还没有调理项目'}
          emptyIcon={
            <svg className="h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          }
          emptyAction={
            canManageTreatments && !searchTerm ? (
              <Button onClick={handleAddNewTreatment} className="mt-4">
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加第一个调理项目
              </Button>
            ) : null
          }
        />

        {/* Add/Edit Treatment Modal */}
        <Modal
          isOpen={showTreatmentModal}
          onClose={() => setShowTreatmentModal(false)}
          title={selectedTreatment ? '编辑调理项目' : '添加新调理项目'}
          size="large"
          footer={
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowTreatmentModal(false)}
              >
                取消
              </Button>
              <Button
                type="submit"
                form="treatment-form"
              >
                {selectedTreatment ? '保存修改' : '添加项目'}
              </Button>
            </div>
          }
        >
          {modalLoading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="animate-spin h-8 w-8 text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            </div>
          ) : (
            <form id="treatment-form" onSubmit={handleSubmit} className="space-y-6">
              {formErrors.submit && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {formErrors.submit}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Type selection */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">项目类型</label>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="type"
                        value="basic"
                        checked={formData.type === 'basic'}
                        onChange={handleInputChange}
                        className="form-radio text-blue-600"
                      />
                      <span className="ml-2">基础项目</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="type"
                        value="project"
                        checked={formData.type === 'project'}
                        onChange={handleInputChange}
                        className="form-radio text-purple-600"
                      />
                      <span className="ml-2">特色项目</span>
                    </label>
                  </div>
                </div>

                {/* Name */}
                <div className="md:col-span-2">
                  <Input
                    label="项目名称"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={formErrors.name}
                    required
                    placeholder="请输入调理项目名称"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">项目描述</label>
                  <textarea
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                    placeholder="描述此调理项目的功效、适应症、注意事项等"
                  />
                </div>

                {/* Duration */}
                <Input
                  label="时长 (分钟)"
                  name="duration"
                  type="number"
                  min="5"
                  max="480"
                  value={formData.duration}
                  onChange={handleInputChange}
                  error={formErrors.duration}
                  required
                  placeholder="项目时长"
                />

                {/* Price */}
                <Input
                  label="价格 (元)"
                  name="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleInputChange}
                  error={formErrors.price}
                  placeholder="项目价格"
                />

                {/* Practitioners (optional, if you have a list) */}
                {/* ... */}

                {/* Applicable Conditions */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">适应症（用顿号分隔）</label>
                  <Input
                    name="applicableConditions"
                    value={formData.applicableConditions}
                    onChange={handleInputChange}
                    placeholder="如：体质虚弱、慢性疾病、免疫力低下"
                  />
                </div>

                {/* Contraindications */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">禁忌症（用顿号分隔）</label>
                  <Input
                    name="contraindications"
                    value={formData.contraindications}
                    onChange={handleInputChange}
                    placeholder="如：严重过敏、急性疾病"
                  />
                </div>

                {/* Preparation Instructions */}
                <div className="md:col-span-2">
                  <Input
                    label="注意事项"
                    name="preparationInstructions"
                    value={formData.preparationInstructions}
                    onChange={handleInputChange}
                    placeholder="如：治疗前避免饮用冷饮"
                  />
                </div>

                {/* Post Treatment Care */}
                <div className="md:col-span-2">
                  <Input
                    label="调理后建议"
                    name="postTreatmentCare"
                    value={formData.postTreatmentCare}
                    onChange={handleInputChange}
                    placeholder="如：调理后注意休息，避免剧烈运动"
                  />
                </div>

                {/* composedOf for 特色项目 only */}
                {formData.type === 'project' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">包含基础项目</label>
                    <div className="flex flex-wrap gap-2">
                      {treatments.filter(t => t.type === 'basic').map(t => {
                        const selected = (formData.composedOf || []).includes(t.id);
                        return (
                          <button
                            type="button"
                            key={t.id}
                            className={
                              'px-3 py-1 rounded-full text-xs font-medium border transition ' +
                              (selected
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-emerald-50 hover:border-emerald-200')
                            }
                            onClick={() => {
                              setFormData(prev => {
                                const composedOf = prev.composedOf || [];
                                if (composedOf.includes(t.id)) {
                                  return { ...prev, composedOf: composedOf.filter(id => id !== t.id) };
                                } else {
                                  return { ...prev, composedOf: [...composedOf, t.id] };
                                }
                              });
                            }}
                          >
                            {t.name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">点击可多选，已选中高亮</div>
                  </div>
                )}

                {/* isActive */}
                <div className="md:col-span-2">
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <input
                      id="isActive"
                      name="isActive"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 focus:ring-offset-0"
                    />
                    <div>
                      <label htmlFor="isActive" className="text-sm font-medium text-gray-900">
                        启用此调理项目
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        启用后，该项目将在预约系统中可选
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default TreatmentsPage;